import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, Layers, Target, AlertTriangle, Shield, CheckCircle,
  Link2, Clock, User, BarChart2, TrendingUp, PieChart, RefreshCw,
  ChevronDown, ChevronRight, Eye, Edit2, Filter, Save, X, Plus, Trash2
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

// ==========================================
// MONDAY.COM-INSPIRED STAT CARD
// ==========================================

const StatCard = ({ label, value, icon: Icon, color, subValue, onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all cursor-pointer group ${
      onClick ? 'hover:border-purple-300' : ''
    }`}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color || 'text-gray-900'}`}>{value}</p>
        {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
      </div>
      <div className={`p-2 rounded-lg ${color?.replace('text-', 'bg-').replace('-600', '-100') || 'bg-gray-100'}`}>
        <Icon className={`w-5 h-5 ${color || 'text-gray-600'}`} />
      </div>
    </div>
  </div>
);

// ==========================================
// EDITABLE ROW COMPONENT
// ==========================================

const EditableRow = ({ item, onSave, onDelete, type }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.text || item.description || '');
  const [severity, setSeverity] = useState(item.severity || 'medium');
  
  const handleSave = () => {
    onSave?.({
      ...item,
      text: editValue,
      description: editValue,
      severity: type === 'risk' ? severity : undefined,
    });
    setIsEditing(false);
  };
  
  if (isEditing) {
    return (
      <tr className="bg-purple-50">
        <td className="px-4 py-3" colSpan={type === 'risk' ? 1 : 2}>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-3 py-1.5 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              autoFocus
              data-testid={`edit-${type}-input`}
            />
            {type === 'risk' && (
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="px-2 py-1.5 border border-purple-300 rounded-lg text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            )}
            <button
              onClick={handleSave}
              className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600"
              data-testid={`save-${type}-btn`}
            >
              <Save className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="p-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </td>
        {type === 'risk' && <td className="px-4 py-3" />}
      </tr>
    );
  }
  
  return null; // Handled by render function
};

// ==========================================
// DATA TABLE WITH MONDAY.COM STYLE + EDITING
// ==========================================

const DataTable = ({ 
  title, 
  icon: Icon, 
  items, 
  columns, 
  emptyMessage, 
  color,
  editable = false,
  onEdit,
  onDelete,
  onAdd,
  itemType,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editSeverity, setEditSeverity] = useState('medium');
  const [showAddRow, setShowAddRow] = useState(false);
  const [newItemValue, setNewItemValue] = useState('');
  const [newItemSeverity, setNewItemSeverity] = useState('medium');
  
  const handleStartEdit = (index, item) => {
    setEditingIndex(index);
    setEditValue(item.text || item.description || '');
    setEditSeverity(item.severity || 'medium');
  };
  
  const handleSaveEdit = (item) => {
    onEdit?.({
      ...item,
      text: editValue,
      description: editValue,
      severity: itemType === 'risk' ? editSeverity : undefined,
    });
    setEditingIndex(null);
    setEditValue('');
  };
  
  const handleAddNew = () => {
    if (!newItemValue.trim()) return;
    onAdd?.({
      text: newItemValue,
      description: newItemValue,
      severity: itemType === 'risk' ? newItemSeverity : undefined,
    });
    setNewItemValue('');
    setShowAddRow(false);
  };
  
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid={`table-${title.toLowerCase().replace(/\s/g, '-')}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 ${color?.replace('text-', 'border-l-4 border-') || 'border-l-4 border-gray-300'}`}>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <Icon className={`w-5 h-5 ${color || 'text-gray-600'}`} />
          <span className="font-semibold text-gray-900">{title}</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {items.length}
          </span>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {editable && isExpanded && (
          <button
            onClick={() => setShowAddRow(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            data-testid={`add-${itemType}-btn`}
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        )}
      </div>
      
      {/* Content */}
      {isExpanded && (
        <div className="border-t">
          {items.length === 0 && !showAddRow ? (
            <div className="p-4 text-center">
              <p className="text-sm text-gray-500 italic mb-2">{emptyMessage}</p>
              {editable && (
                <button
                  onClick={() => setShowAddRow(true)}
                  className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Add first {itemType}
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  {columns.map((col, i) => (
                    <th key={i} className="px-4 py-2 text-left font-medium">{col.header}</th>
                  ))}
                  {editable && <th className="px-4 py-2 text-right font-medium w-24">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Add New Row */}
                {showAddRow && (
                  <tr className="bg-green-50">
                    <td className="px-4 py-3" colSpan={itemType === 'risk' ? 1 : columns.length}>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newItemValue}
                          onChange={(e) => setNewItemValue(e.target.value)}
                          placeholder={`Enter new ${itemType}...`}
                          className="flex-1 px-3 py-1.5 border border-green-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
                          data-testid={`new-${itemType}-input`}
                        />
                        {itemType === 'risk' && (
                          <select
                            value={newItemSeverity}
                            onChange={(e) => setNewItemSeverity(e.target.value)}
                            className="px-2 py-1.5 border border-green-300 rounded-lg text-sm"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        )}
                      </div>
                    </td>
                    {itemType === 'risk' && <td />}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={handleAddNew}
                          disabled={!newItemValue.trim()}
                          className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                          data-testid={`confirm-add-${itemType}-btn`}
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setShowAddRow(false); setNewItemValue(''); }}
                          className="p-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* Existing Items */}
                {items.map((item, i) => (
                  <tr key={i} className={`hover:bg-gray-50 transition-colors ${editingIndex === i ? 'bg-purple-50' : ''}`}>
                    {editingIndex === i ? (
                      <>
                        <td className="px-4 py-3" colSpan={itemType === 'risk' ? 1 : columns.length}>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 px-3 py-1.5 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                              autoFocus
                              onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit(item)}
                              data-testid={`edit-${itemType}-input`}
                            />
                            {itemType === 'risk' && (
                              <select
                                value={editSeverity}
                                onChange={(e) => setEditSeverity(e.target.value)}
                                className="px-2 py-1.5 border border-purple-300 rounded-lg text-sm"
                              >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            )}
                          </div>
                        </td>
                        {itemType === 'risk' && <td />}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleSaveEdit(item)}
                              className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600"
                              data-testid={`save-edit-${itemType}-btn`}
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingIndex(null)}
                              className="p-1.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        {columns.map((col, j) => (
                          <td key={j} className="px-4 py-3">
                            {col.render ? col.render(item) : item[col.accessor]}
                          </td>
                        ))}
                        {editable && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 hover:opacity-100">
                              <button
                                onClick={() => handleStartEdit(i, item)}
                                className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                data-testid={`edit-${itemType}-${i}-btn`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => onDelete?.(item)}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`delete-${itemType}-${i}-btn`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// CATEGORY BREAKDOWN CHART
// ==========================================

const CategoryBreakdown = ({ data }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  const colors = {
    'Functional': 'bg-blue-500',
    'Non-Functional': 'bg-purple-500',
    'Integration': 'bg-cyan-500',
    'Reporting': 'bg-amber-500',
    'Compliance': 'bg-red-500',
    'UX': 'bg-pink-500',
    'Technical': 'bg-gray-500',
  };
  
  if (total === 0) return null;
  
  return (
    <div className="bg-white rounded-xl border p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <PieChart className="w-4 h-4 text-purple-600" />
        Category Breakdown
      </h3>
      <div className="space-y-2">
        {Object.entries(data).filter(([_, v]) => v > 0).map(([category, count]) => (
          <div key={category} className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${colors[category] || 'bg-gray-400'}`} />
            <span className="text-sm text-gray-700 flex-1">{category}</span>
            <span className="text-sm font-medium text-gray-900">{count}</span>
            <span className="text-xs text-gray-400">({Math.round(count/total*100)}%)</span>
          </div>
        ))}
      </div>
      {/* Visual bar */}
      <div className="flex h-2 rounded-full overflow-hidden mt-3">
        {Object.entries(data).filter(([_, v]) => v > 0).map(([category, count]) => (
          <div 
            key={category} 
            className={`${colors[category] || 'bg-gray-400'}`}
            style={{ width: `${(count/total)*100}%` }}
          />
        ))}
      </div>
    </div>
  );
};

// ==========================================
// MAIN SCOPE SUMMARY COMPONENT
// ==========================================

const ScopeSummary = ({ projectId, tenantId, requirements = [], onNavigateToRequirement, onDataChanged }) => {
  const [loading, setLoading] = useState(false);
  
  // Flatten all requirements from tree structure
  const flattenRequirements = (items, result = []) => {
    items.forEach(item => {
      result.push(item);
      if (item.children?.length) {
        flattenRequirements(item.children, result);
      }
    });
    return result;
  };
  
  const allRequirements = useMemo(() => flattenRequirements(requirements), [requirements]);
  
  // Edit handlers for inline editing
  const handleEditItem = async (type, item, updatedData) => {
    if (!item.requirement?.id) return;
    
    setLoading(true);
    try {
      // Get the requirement
      const reqId = item.requirement.id;
      const requirement = allRequirements.find(r => r.id === reqId);
      if (!requirement) {
        toast.error('Requirement not found');
        return;
      }
      
      // Build update payload based on type
      let updatePayload = {};
      
      if (type === 'risk') {
        // Update the specific risk in the array
        const updatedRisks = (requirement.risks || []).map((r, i) => {
          if ((r.description || r.text) === (item.description || item.text)) {
            return { description: updatedData.text, severity: updatedData.severity };
          }
          return r;
        });
        updatePayload = { risks: updatedRisks };
      } else if (type === 'ac') {
        // Update the specific AC in the array
        const updatedACs = (requirement.acceptance_criteria || []).map(ac => {
          const acText = typeof ac === 'string' ? ac : (ac.content || ac.given);
          if (acText === item.text || (item.rawAC && JSON.stringify(ac) === JSON.stringify(item.rawAC))) {
            return updatedData.text;
          }
          return ac;
        });
        updatePayload = { acceptance_criteria: updatedACs };
      } else if (type === 'constraint') {
        // Update the specific constraint in the array
        const updatedConstraints = (requirement.constraints || []).map(c => {
          const cText = typeof c === 'string' ? c : (c.description || c.text);
          if (cText === item.text) {
            return updatedData.text;
          }
          return c;
        });
        updatePayload = { constraints: updatedConstraints };
      }
      
      await api.updateRequirement(tenantId, reqId, updatePayload);
      toast.success(`${type.toUpperCase()} updated`);
      onDataChanged?.(); // Trigger refresh
    } catch (err) {
      console.error(`Failed to update ${type}:`, err);
      toast.error(`Failed to update ${type}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteItem = async (type, item) => {
    if (!item.requirement?.id) return;
    
    setLoading(true);
    try {
      const reqId = item.requirement.id;
      const requirement = allRequirements.find(r => r.id === reqId);
      if (!requirement) {
        toast.error('Requirement not found');
        return;
      }
      
      let updatePayload = {};
      
      if (type === 'risk') {
        const updatedRisks = (requirement.risks || []).filter(r => 
          (r.description || r.text) !== (item.description || item.text)
        );
        updatePayload = { risks: updatedRisks };
      } else if (type === 'ac') {
        const updatedACs = (requirement.acceptance_criteria || []).filter(ac => {
          const acText = typeof ac === 'string' ? ac : (ac.content || ac.given);
          return acText !== item.text;
        });
        updatePayload = { acceptance_criteria: updatedACs };
      } else if (type === 'constraint') {
        const updatedConstraints = (requirement.constraints || []).filter(c => {
          const cText = typeof c === 'string' ? c : (c.description || c.text);
          return cText !== item.text;
        });
        updatePayload = { constraints: updatedConstraints };
      }
      
      await api.updateRequirement(tenantId, reqId, updatePayload);
      toast.success(`${type.toUpperCase()} deleted`);
      onDataChanged?.();
    } catch (err) {
      console.error(`Failed to delete ${type}:`, err);
      toast.error(`Failed to delete ${type}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddItem = async (type, item, requirementId) => {
    // For adding new items, we need a target requirement
    // This will be handled via a modal or selecting requirement first
    toast.info(`To add a ${type}, select a requirement from the Requirements tab`);
  };
  
  // Calculate statistics
  const stats = useMemo(() => {
    const themes = allRequirements.filter(r => r.hierarchy_type === 'theme');
    const epics = allRequirements.filter(r => r.hierarchy_type === 'epic');
    const reqs = allRequirements.filter(r => r.hierarchy_type === 'requirement');
    
    // Aggregate all data
    const allRisks = [];
    const allACs = [];
    const allConstraints = [];
    const allDependencies = [];
    const categories = {};
    let totalQuality = 0;
    let qualityCount = 0;
    
    allRequirements.forEach(r => {
      // Risks
      if (r.risks?.length) {
        r.risks.forEach(risk => {
          // Handle both string and object risks
          const riskObj = typeof risk === 'string' 
            ? { description: risk } 
            : risk;
          allRisks.push({ ...riskObj, requirement: r });
        });
      }
      // Acceptance Criteria
      if (r.acceptance_criteria?.length) {
        r.acceptance_criteria.forEach(ac => {
          // Handle both string and object ACs (with given/when/then format)
          let acText = '';
          if (typeof ac === 'string') {
            acText = ac;
          } else if (ac.content) {
            acText = ac.content;
          } else if (ac.given || ac.when || ac.then) {
            acText = `GIVEN ${ac.given || '...'} WHEN ${ac.when || '...'} THEN ${ac.then || '...'}`;
          } else {
            acText = JSON.stringify(ac);
          }
          allACs.push({ text: acText, requirement: r, rawAC: ac });
        });
      }
      // Constraints
      if (r.constraints?.length) {
        r.constraints.forEach(c => {
          const constraintText = typeof c === 'string' ? c : (c.description || c.text || JSON.stringify(c));
          allConstraints.push({ text: constraintText, requirement: r });
        });
      }
      // Dependencies
      if (r.dependencies?.length) {
        r.dependencies.forEach(d => {
          const depObj = typeof d === 'string' ? { target_id: d } : d;
          allDependencies.push({ ...depObj, requirement: r });
        });
      }
      // Categories
      if (r.category) {
        categories[r.category] = (categories[r.category] || 0) + 1;
      }
      // Quality
      if (r.quality_flags?.score) {
        totalQuality += r.quality_flags.score;
        qualityCount++;
      }
    });
    
    return {
      themes: themes.length,
      epics: epics.length,
      requirements: reqs.length,
      total: allRequirements.length,
      risks: allRisks,
      acceptanceCriteria: allACs,
      constraints: allConstraints,
      dependencies: allDependencies,
      categories,
      avgQuality: qualityCount > 0 ? Math.round(totalQuality / qualityCount) : 0,
      needsWork: allRequirements.filter(r => 
        r.quality_flags?.missing_ac || 
        r.quality_flags?.missing_category || 
        r.quality_flags?.ambiguous_language
      ),
    };
  }, [allRequirements]);

  return (
    <div className="space-y-6" data-testid="scope-summary">
      {/* Stats Grid - Monday.com style */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard 
          label="Themes" 
          value={stats.themes} 
          icon={Layers} 
          color="text-purple-600"
        />
        <StatCard 
          label="Epics" 
          value={stats.epics} 
          icon={Target} 
          color="text-blue-600"
        />
        <StatCard 
          label="Requirements" 
          value={stats.requirements} 
          icon={FileText} 
          color="text-green-600"
        />
        <StatCard 
          label="Risks" 
          value={stats.risks.length} 
          icon={AlertTriangle} 
          color="text-amber-600"
          subValue={stats.risks.length > 0 ? "Click to view" : undefined}
        />
        <StatCard 
          label="Constraints" 
          value={stats.constraints.length} 
          icon={Shield} 
          color="text-blue-600"
        />
        <StatCard 
          label="Avg Quality" 
          value={`${stats.avgQuality}%`} 
          icon={BarChart2} 
          color={stats.avgQuality >= 80 ? "text-green-600" : stats.avgQuality >= 60 ? "text-amber-600" : "text-red-600"}
        />
      </div>

      {/* Category Breakdown & Quality */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryBreakdown data={stats.categories} />
        
        {/* Needs Work */}
        {stats.needsWork.length > 0 && (
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Needs Attention ({stats.needsWork.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {stats.needsWork.slice(0, 5).map((r, i) => (
                <button
                  key={i}
                  onClick={() => onNavigateToRequirement?.(r)}
                  className="w-full text-left p-2 bg-white rounded-lg border border-amber-100 hover:border-amber-300 transition-colors text-sm"
                >
                  <span className="font-medium text-amber-900">{r.name}</span>
                  <div className="flex gap-2 mt-1">
                    {r.quality_flags?.missing_ac && (
                      <span className="text-xs text-amber-600">Missing AC</span>
                    )}
                    {r.quality_flags?.missing_category && (
                      <span className="text-xs text-amber-600">No Category</span>
                    )}
                  </div>
                </button>
              ))}
              {stats.needsWork.length > 5 && (
                <p className="text-xs text-amber-600 text-center">
                  +{stats.needsWork.length - 5} more items need attention
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Data Tables */}
      <div className="space-y-4">
        {/* Acceptance Criteria */}
        <DataTable
          title="Acceptance Criteria"
          icon={CheckCircle}
          items={stats.acceptanceCriteria}
          color="text-green-600"
          emptyMessage="No acceptance criteria defined yet. Add AC to your requirements."
          editable={true}
          itemType="ac"
          onEdit={(item) => handleEditItem('ac', item, item)}
          onDelete={(item) => handleDeleteItem('ac', item)}
          columns={[
            { 
              header: 'Criteria', 
              accessor: 'text',
              render: (item) => (
                <span className="text-gray-700">{item.text}</span>
              )
            },
            { 
              header: 'Requirement', 
              render: (item) => (
                <button 
                  onClick={() => onNavigateToRequirement?.(item.requirement)}
                  className="text-purple-600 hover:text-purple-800 hover:underline text-sm"
                >
                  {item.requirement?.name}
                </button>
              )
            },
          ]}
        />

        {/* Risks */}
        <DataTable
          title="Identified Risks"
          icon={AlertTriangle}
          items={stats.risks}
          color="text-amber-600"
          emptyMessage="No risks identified yet. Use AI Copilot to identify potential risks."
          editable={true}
          itemType="risk"
          onEdit={(item) => handleEditItem('risk', item, item)}
          onDelete={(item) => handleDeleteItem('risk', item)}
          columns={[
            { 
              header: 'Risk', 
              render: (item) => (
                <div>
                  <span className="text-gray-700">{item.description || item.text || 'Unnamed risk'}</span>
                  {item.severity && (
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                      item.severity === 'high' ? 'bg-red-100 text-red-700' :
                      item.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.severity}
                    </span>
                  )}
                </div>
              )
            },
            { 
              header: 'Requirement', 
              render: (item) => (
                <button 
                  onClick={() => onNavigateToRequirement?.(item.requirement)}
                  className="text-purple-600 hover:text-purple-800 hover:underline text-sm"
                >
                  {item.requirement?.name}
                </button>
              )
            },
          ]}
        />

        {/* Constraints */}
        <DataTable
          title="Constraints"
          icon={Shield}
          items={stats.constraints}
          color="text-blue-600"
          emptyMessage="No constraints defined. Add technical or business constraints to your requirements."
          editable={true}
          itemType="constraint"
          onEdit={(item) => handleEditItem('constraint', item, item)}
          onDelete={(item) => handleDeleteItem('constraint', item)}
          columns={[
            { 
              header: 'Constraint', 
              render: (item) => (
                <span className="text-gray-700">{item.text}</span>
              )
            },
            { 
              header: 'Requirement', 
              render: (item) => (
                <button 
                  onClick={() => onNavigateToRequirement?.(item.requirement)}
                  className="text-purple-600 hover:text-purple-800 hover:underline text-sm"
                >
                  {item.requirement?.name}
                </button>
              )
            },
          ]}
        />

        {/* Dependencies */}
        <DataTable
          title="Dependencies"
          icon={Link2}
          items={stats.dependencies}
          color="text-purple-600"
          emptyMessage="No dependencies mapped. Link related requirements together."
          columns={[
            { 
              header: 'Dependency', 
              render: (item) => (
                <span className="text-gray-700">
                  {item.target_name || item.target_id || 'Unknown'}
                  {item.type && <span className="text-xs text-gray-400 ml-2">({item.type})</span>}
                </span>
              )
            },
            { 
              header: 'From', 
              render: (item) => (
                <button 
                  onClick={() => onNavigateToRequirement?.(item.requirement)}
                  className="text-purple-600 hover:text-purple-800 hover:underline text-sm"
                >
                  {item.requirement?.name}
                </button>
              )
            },
          ]}
        />
      </div>
      
      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
            <span className="text-gray-700">Saving changes...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeSummary;
